/**
 * Legal waiver gate. Shown on first launch before onboarding; the user
 * must tick four checkboxes acknowledging:
 *   1. The site is provided as-is, no warranty.
 *   2. No image leaves the device — everything runs locally.
 *   3. Privacy Filter is experimental, not a substitute for analysis.
 *   4. They've read OpenAI's release conditions on Hugging Face.
 *
 * Acceptance is persisted in localStorage; subsequent visits skip
 * straight to onboarding / the entry screen.
 */

const ACCEPTED_KEY = "redact_legal_accepted_v1";
const HF_URL = "https://huggingface.co/openai/privacy-filter";

export function isLegalAccepted(): boolean {
  try {
    return localStorage.getItem(ACCEPTED_KEY) === "1";
  } catch {
    return false;
  }
}

function markAccepted(): void {
  try {
    localStorage.setItem(ACCEPTED_KEY, "1");
  } catch {
    /* private mode — gate will reappear on every visit, that's fine */
  }
}

const CLAUSES: ReadonlyArray<{ id: string; html: string }> = [
  {
    id: "as-is",
    html:
      "I understand that this website is provided <strong>as-is</strong>, " +
      "with <strong>no warranty</strong>, and I release the author from any " +
      "consequence of using this web application.",
  },
  {
    id: "local",
    html:
      "I understand that the website is configured so it will <strong>not " +
      "receive any image I pick</strong>, and that all data is processed " +
      "locally on my machine.",
  },
  {
    id: "experimental",
    html:
      "I understand <a href=\"" + HF_URL + "\" target=\"_blank\" rel=\"noopener\">Privacy Filter</a> " +
      "to be <strong>experimental technology and a preview model</strong>, " +
      "not a substitute for any real analysis.",
  },
  {
    id: "openai",
    html:
      "I have read and understood the conditions under which OpenAI Inc. " +
      "has released these models as described at " +
      "<a href=\"" + HF_URL + "\" target=\"_blank\" rel=\"noopener\">" +
      "huggingface.co/openai/privacy-filter</a>.",
  },
];

export interface RunLegalOptions {
  root: HTMLElement;
}

/**
 * Mount the waiver. Resolves once the user has ticked all boxes and
 * pressed Continue. The promise rejects if the user clicks Decline.
 */
export function runLegalWaiver(opts: RunLegalOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    opts.root.innerHTML = "";

    const shell = document.createElement("div");
    shell.className = "legal-shell";
    shell.innerHTML = `
      <header class="legal-header">
        <span class="legal-eyebrow">Step 1 of 3</span>
        <h1 class="legal-title serif">Read this. Tick each box.</h1>
        <p class="legal-lede">
          Redact runs entirely in your browser. There's no account, no upload,
          no telemetry — but there's also no warranty. Please acknowledge each
          point before continuing.
        </p>
      </header>

      <ul class="legal-list" id="legal-list">
        ${CLAUSES.map((c, i) => `
          <li class="legal-item">
            <label>
              <input type="checkbox" data-clause="${c.id}" />
              <span class="legal-check" aria-hidden="true"></span>
              <span class="legal-text">
                <span class="legal-num">${i + 1}.</span>
                <span class="legal-prose serif">${c.html}</span>
              </span>
            </label>
          </li>
        `).join("")}
      </ul>

      <footer class="legal-footer">
        <button id="decline" class="pill pill-ghost" type="button">Decline</button>
        <button id="continue" class="pill pill-primary" type="button" disabled>Continue</button>
      </footer>
    `;
    opts.root.appendChild(shell);

    const checkboxes = Array.from(shell.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
    const continueBtn = shell.querySelector<HTMLButtonElement>("#continue")!;
    const declineBtn = shell.querySelector<HTMLButtonElement>("#decline")!;

    const refresh = () => {
      const allChecked = checkboxes.every((c) => c.checked);
      continueBtn.disabled = !allChecked;
      // Also update each item's visual state for the bronze stripe.
      for (const cb of checkboxes) {
        const item = cb.closest(".legal-item");
        if (item) item.classList.toggle("is-checked", cb.checked);
      }
    };
    for (const cb of checkboxes) {
      cb.addEventListener("change", refresh);
    }
    refresh();

    continueBtn.addEventListener("click", () => {
      markAccepted();
      resolve();
    });
    declineBtn.addEventListener("click", () => {
      reject(new Error("legal-declined"));
    });
  });
}

/**
 * Render a final wall when the user declines. There's nowhere else to
 * go on a no-account static SPA, so just explain and offer to retry.
 */
export function renderLegalDeclined(root: HTMLElement, onRetry: () => void): void {
  root.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.className = "legal-declined";
  wrap.innerHTML = `
    <div class="legal-declined-card">
      <h2 class="serif">No worries.</h2>
      <p class="serif">
        Redact won't run unless you've acknowledged each point. If you change
        your mind, head back to the waiver and tick the boxes — nothing about
        you has been recorded.
      </p>
      <p class="serif legal-declined-source">
        Prefer to verify it yourself? The full source lives at
        <a href="https://github.com/eordano/redact-pii" target="_blank" rel="noopener">github.com/eordano/redact-pii</a>
        — clone it and build your own copy, or point an agent at the repo and
        have it read the waiver back to you.
      </p>
      <button id="retry" type="button" class="pill pill-primary">Read the waiver again</button>
    </div>
  `;
  root.appendChild(wrap);
  wrap.querySelector<HTMLButtonElement>("#retry")!.addEventListener("click", onRetry);
}
